import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Plus, Trash, TrashSimple, CaretDown, CaretUp, Copy, MarkdownLogo, LockKey,
} from "@phosphor-icons/react";
import { copyText, copyAsMarkdown, asMarkdown } from "@/lib/clipboard";

// ---------- Debounce helper (mirrors EducationSection) ----------
function useDebounced(fn, delay = 700) {
  const ref = useRef(fn);
  const timer = useRef(null);
  useEffect(() => { ref.current = fn; }, [fn]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const debounced = (...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => ref.current(...args), delay);
  };
  debounced.cancel = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  return debounced;
}

const FIELDS = [
  { key: "name",             label: "Name",           type: "input",    required: true },
  { key: "role",             label: "Role / Title",   type: "input" },
  { key: "company",          label: "Company",        type: "input" },
  { key: "email",            label: "Email",          type: "input" },
  { key: "phone",            label: "Phone",          type: "input" },
  { key: "relationship",     label: "Relationship",   type: "input",    placeholder: "e.g. line manager, tutor, mentor" },
  { key: "contactable_now",  label: "Safe to contact before offer?", type: "checkbox",
    checkboxHint: "yes — okay to contact pre-offer (most refs only want contacting at offer stage)" },
  { key: "notes",            label: "Notes",          type: "textarea", placeholder: "e.g. 'will speak to my reliability and team-fit'" },
];

const formatEntry = (r) =>
  `${r.name || ""}${r.role ? ` — ${r.role}` : ""}${r.company ? `, ${r.company}` : ""}` +
  (r.email ? `\n${r.email}` : "") +
  (r.phone ? `\n${r.phone}` : "") +
  (r.relationship ? `\nRelationship: ${r.relationship}` : "");

// ---------- One Reference entry card (always inline-editable) ----------
function ReferenceCard({ entry, onChange, onDelete, collapsed, onCollapseToggle }) {
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const persist = useDebounced(async (next) => {
    setSaving(true);
    try {
      await api.put(`/references/${next.id}`, next);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } catch {
      toast.error("Couldn't save changes");
    } finally { setSaving(false); }
  }, 700);

  const lockIn = async () => {
    if (!entry.name) return toast.error("Name is required");
    persist.cancel?.();
    setSaving(true);
    try {
      await api.put(`/references/${entry.id}`, entry);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
      toast.success("Locked into the vault");
      onCollapseToggle?.(true);
    } catch {
      toast.error("Couldn't lock it in — try again");
    } finally { setSaving(false); }
  };

  const update = (patch) => {
    const next = { ...entry, ...patch };
    onChange(next);
    setDirty(true);
    persist(next);
  };

  // Collapsed summary — same-shape summary line as before, plus the small badge.
  const badge = entry.contactable_now ? (
    <span className="font-mono-d text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border-[1.5px] border-[var(--brand-ink)] bg-[var(--pastel-mint)]" title="okay to contact pre-offer">contactable</span>
  ) : (
    <span className="font-mono-d text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border-[1.5px] border-[var(--brand-ink)] bg-[var(--pastel-yellow)]" title="only contact at offer stage">offer-stage</span>
  );

  return (
    <li data-testid={`item-references-${entry.id}`}
      className="border-[1.5px] border-[var(--brand-ink)] rounded-md bg-white">
      {/* Card header — always visible, click to expand/collapse */}
      <div className="flex items-start justify-between p-4 gap-3">
        <button onClick={() => onCollapseToggle?.(!collapsed)}
          data-testid={`toggle-references-${entry.id}`}
          className="flex-1 text-left min-w-0">
          <p className="font-medium flex items-center gap-2 flex-wrap">
            <span className="truncate">{entry.name || <span className="text-[var(--brand-muted)] italic">unnamed reference</span>}</span>
            {entry.name && badge}
            {saving && <span className="font-mono-d text-[10px] text-[var(--brand-muted)]">saving…</span>}
            {justSaved && !saving && <span className="font-mono-d text-[10px] text-[color:var(--matcha-strong,#557755)]">saved ✓</span>}
            {dirty && !saving && !justSaved && <span className="font-mono-d text-[10px] text-[color:var(--brand-primary)]">unsaved</span>}
          </p>
          <p className="text-xs font-mono-d text-[var(--brand-muted)] mt-1">
            {entry.role}{entry.company ? ` · ${entry.company}` : ""}
            {(entry.email || entry.phone) ? ` · ${entry.email || ""}${entry.email && entry.phone ? " · " : ""}${entry.phone || ""}` : ""}
          </p>
        </button>
        <div className="flex gap-1 shrink-0">
          <button onClick={() => copyText(formatEntry(entry))} title="copy entry"
            data-testid={`copy-references-${entry.id}`}
            className="p-1.5 hover:bg-[var(--pastel-yellow)] rounded"><Copy size={16}/></button>
          <button onClick={() => copyAsMarkdown(asMarkdown.reference, entry, "Copied as Markdown")}
            title="copy as Markdown" data-testid={`copy-md-references-${entry.id}`}
            className="p-1.5 hover:bg-[var(--pastel-mint)] rounded"><MarkdownLogo size={16}/></button>
          <button onClick={() => onCollapseToggle?.(!collapsed)}
            className="p-1.5 hover:bg-[var(--pastel-yellow)] rounded"
            title={collapsed ? "expand" : "collapse"}>
            {collapsed ? <CaretDown size={16}/> : <CaretUp size={16}/>}
          </button>
          <button onClick={onDelete} data-testid={`delete-references-${entry.id}`}
            className="p-1.5 hover:bg-[var(--pastel-peach)] rounded"><Trash size={16}/></button>
        </div>
      </div>

      {/* Expanded body — inline-editable fields (no modal) */}
      {!collapsed && (
        <div className="border-t-[1.5px] border-[var(--brand-ink)] p-4 bg-[var(--pastel-yellow)]"
          data-testid={`editor-references-${entry.id}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                <label className="label-tiny block mb-1.5">{f.label}{f.required ? " *" : ""}</label>
                {f.type === "textarea" ? (
                  <textarea data-testid={`field-references-${f.key}`}
                    value={entry[f.key] || ""}
                    onChange={(e) => update({ [f.key]: e.target.value })}
                    placeholder={f.placeholder || ""}
                    className="w-full card-hard rounded-md px-3 py-2 min-h-[80px] bg-white"/>
                ) : f.type === "checkbox" ? (
                  <label className="card-hard rounded-md px-3 py-2 bg-white flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" data-testid={`field-references-${f.key}`}
                      checked={!!entry[f.key]}
                      onChange={(e) => update({ [f.key]: e.target.checked })}/>
                    <span>{f.checkboxHint || f.label}</span>
                  </label>
                ) : (
                  <input data-testid={`field-references-${f.key}`}
                    value={entry[f.key] || ""}
                    onChange={(e) => update({ [f.key]: e.target.value })}
                    placeholder={f.placeholder || ""}
                    className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
                )}
              </div>
            ))}
          </div>
          <div className="mt-5 flex gap-2 items-center">
            <button onClick={lockIn} data-testid={`lock-in-references-${entry.id}`}
              disabled={saving}
              className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2 text-sm flex items-center gap-1.5 hover:bg-[var(--brand-primary)]">
              <LockKey size={14} weight="bold"/> {saving ? "locking…" : "lock it in"}
            </button>
            <span className="text-xs text-[var(--brand-muted)]">
              auto-saves as you type · hit <b>lock it in</b> when you&apos;re done
            </span>
          </div>
        </div>
      )}
    </li>
  );
}

// ---------- Section wrapper ----------
export default function ReferencesSection() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());

  const load = async () => {
    const { data } = await api.get("/references");
    setItems(data);
    // Land collapsed by default for tidiness.
    setCollapsed(new Set(data.map((e) => e.id)));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/references", { name: "New reference" });
      setItems((cur) => [data, ...cur]);
      // New cards should open expanded so the user can start typing immediately.
      setCollapsed((cur) => {
        const next = new Set(cur);
        next.delete(data.id);
        return next;
      });
      toast.success("New reference added — start filling it in");
    } catch {
      toast.error("Couldn't add reference");
    } finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete reference?")) return;
    try {
      await api.delete(`/references/${id}`);
      setItems((cur) => cur.filter((e) => e.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Couldn't delete"); }
  };

  const clearAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Delete all ${items.length} references? This can't be undone.`)) return;
    try {
      const { data } = await api.delete("/vault/clear/references");
      setItems([]);
      toast.success(`Wiped ${data.deleted} references`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't clear");
    }
  };

  const onEntryChange = (next) => {
    setItems((cur) => cur.map((r) => (r.id === next.id ? next : r)));
  };
  const onCollapseToggle = (id, should) => {
    setCollapsed((cur) => {
      const next = new Set(cur);
      if (should) next.add(id); else next.delete(id);
      return next;
    });
  };

  const allCollapsed = items.length > 0 && items.every((e) => collapsed.has(e.id));
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(items.map((e) => e.id)));

  return (
    <section className="card-hard rounded-md bg-white" data-testid="section-references">
      <div className="flex items-center justify-between p-5 border-b-[1.5px] border-[var(--brand-ink)] bg-[var(--pastel-peach)]">
        <div>
          <p className="label-tiny">/references</p>
          <h2 className="font-display text-2xl font-semibold tracking-tight">References</h2>
          <p className="text-xs text-[var(--brand-ink)]/60 mt-1">
            people who&apos;ll vouch for you. add 2–3 from past employers, mentors, or tutors.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="font-mono-d text-xs text-[var(--brand-muted)] mr-1">
            {items.length} {items.length === 1 ? "reference" : "references"}
          </span>
          {items.length > 1 && (
            <button onClick={allCollapsed ? expandAll : collapseAll}
              data-testid="refs-collapse-all"
              className="btn-hard bg-white rounded-md px-3 py-2 text-xs flex items-center gap-1">
              {allCollapsed ? <CaretDown size={12} weight="bold"/> : <CaretUp size={12} weight="bold"/>}
              {allCollapsed ? "expand all" : "collapse all"}
            </button>
          )}
          {items.length > 0 && (
            <button onClick={clearAll} data-testid="clear-all-references"
              title={`wipe all ${items.length} references`}
              className="btn-hard bg-[#B91C1C] text-white rounded-md px-2.5 py-2 text-xs flex items-center gap-1.5">
              <TrashSimple size={13} weight="bold"/>
              <span className="hidden sm:inline">delete all</span>
            </button>
          )}
          <button onClick={create} disabled={creating} data-testid="add-references"
            className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-3 py-2 text-sm flex items-center gap-1.5 hover:bg-[var(--brand-ink)]">
            <Plus weight="bold" size={14}/> add reference
          </button>
        </div>
      </div>

      <div className="p-5">
        {items.length === 0 ? (
          <p className="text-sm text-[var(--brand-muted)]">no references yet. hit add to put one in.</p>
        ) : (
          <ul className="space-y-3">
            {items.map((r) => (
              <ReferenceCard key={r.id} entry={r}
                onChange={onEntryChange}
                onDelete={() => remove(r.id)}
                collapsed={collapsed.has(r.id)}
                onCollapseToggle={(should) => onCollapseToggle(r.id, should)}/>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
